/**
 * ExpertiseSelector Component
 * Multi-select tag system with categories
 * Search and filter functionality
 * Proficiency level per expertise
 */

import { Plus, Search, X } from 'lucide-react';
import { useState } from 'react';

interface ExpertiseArea {
  name: string;
  category: string;
  proficiency: 'beginner' | 'intermediate' | 'advanced' | 'expert';
}

interface ExpertiseSelectorProps {
  selectedExpertise: ExpertiseArea[];
  onAdd: (name: string, category: string, proficiency: 'beginner' | 'intermediate' | 'advanced' | 'expert') => void;
  onRemove: (index: number) => void;
}

// Expertise kategorileri ve ornekler
const EXPERTISE_CATEGORIES = {
  'Programming Languages': [
    'Python', 'JavaScript', 'TypeScript', 'Rust', 'Go', 'Java', 'C++', 'C#', 'Ruby', 'PHP', 'Swift', 'Kotlin'
  ],
  'Frameworks': [
    'React', 'Vue', 'Angular', 'Next.js', 'Django', 'Flask', 'FastAPI', 'Express', 'Spring', 'Laravel'
  ],
  'Databases': [
    'PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'SQLite', 'Elasticsearch', 'Cassandra', 'DynamoDB'
  ],
  'DevOps': [
    'Docker', 'Kubernetes', 'AWS', 'Azure', 'GCP', 'CI/CD', 'Terraform', 'Ansible', 'Jenkins'
  ],
  'AI/ML': [
    'Machine Learning', 'Deep Learning', 'NLP', 'Computer Vision', 'TensorFlow', 'PyTorch', 'Scikit-learn'
  ],
  'Web Development': [
    'HTML/CSS', 'Responsive Design', 'Web Performance', 'SEO', 'Accessibility', 'PWA'
  ],
  'Mobile Development': [
    'iOS', 'Android', 'React Native', 'Flutter', 'Mobile UI/UX'
  ],
  'Other': [
    'Git', 'Testing', 'Security', 'Architecture', 'API Design', 'Microservices', 'GraphQL', 'REST'
  ]
};

const PROFICIENCY_COLORS = {
  beginner: '#22c55e',
  intermediate: '#3b82f6',
  advanced: '#8b5cf6',
  expert: '#f59e0b'
};

const PROFICIENCY_LABELS = {
  beginner: 'Baslangic',
  intermediate: 'Orta',
  advanced: 'Ileri',
  expert: 'Uzman'
};

export default function ExpertiseSelector({ selectedExpertise, onAdd, onRemove }: ExpertiseSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newExpertise, setNewExpertise] = useState({
    name: '',
    category: 'Programming Languages',
    proficiency: 'intermediate' as 'beginner' | 'intermediate' | 'advanced' | 'expert'
  });

  // Tum expertise'leri flatten et
  const allExpertise = Object.entries(EXPERTISE_CATEGORIES).flatMap(([category, items]) =>
    items.map(name => ({ name, category }))
  );

  // Filtrele
  const filteredExpertise = allExpertise.filter(exp => {
    const matchesSearch = exp.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || exp.category === selectedCategory;
    const notSelected = !selectedExpertise.some(sel => sel.name === exp.name);
    return matchesSearch && matchesCategory && notSelected;
  });

  const handleAdd = () => {
    if (newExpertise.name.trim()) {
      onAdd(newExpertise.name.trim(), newExpertise.category, newExpertise.proficiency);
      setNewExpertise({ name: '', category: 'Programming Languages', proficiency: 'intermediate' });
      setShowAddModal(false);
    }
  };

  const handleQuickAdd = (name: string, category: string) => {
    onAdd(name, category, 'intermediate');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Selected Expertise Tags */}
      {selectedExpertise.length > 0 && (
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>
            Secili Uzmanlik Alanlari ({selectedExpertise.length})
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {selectedExpertise.map((exp, index) => (
              <div
                key={index}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: `${PROFICIENCY_COLORS[exp.proficiency]}15`,
                  border: `1px solid ${PROFICIENCY_COLORS[exp.proficiency]}40`
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  {exp.name}
                </span>
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  background: PROFICIENCY_COLORS[exp.proficiency],
                  color: '#fff'
                }}>
                  {PROFICIENCY_LABELS[exp.proficiency]}
                </span>
                <button
                  onClick={() => onRemove(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <X size={14} color="#64748b" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Uzmanlik alani ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 16px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <Plus size={16} />
          Ozel Ekle
        </button>
      </div>

      {/* Category Filter */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setSelectedCategory('All')}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: selectedCategory === 'All' ? '2px solid #667eea' : '1px solid #e2e8f0',
            background: selectedCategory === 'All' ? '#667eea15' : '#fff',
            color: selectedCategory === 'All' ? '#667eea' : 'var(--text-secondary)',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Tumu
        </button>
        {Object.keys(EXPERTISE_CATEGORIES).map(category => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: selectedCategory === category ? '2px solid #667eea' : '1px solid #e2e8f0',
              background: selectedCategory === category ? '#667eea15' : '#fff',
              color: selectedCategory === category ? '#667eea' : 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Available Expertise */}
      <div>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>
          Mevcut Uzmanlik Alanlari
        </h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '8px',
          maxHeight: '300px',
          overflowY: 'auto',
          padding: '4px'
        }}>
          {filteredExpertise.map((exp, index) => (
            <button
              key={index}
              onClick={() => handleQuickAdd(exp.name, exp.category)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                background: '#fff',
                color: 'var(--text-primary)',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.background = '#667eea08';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-default)';
                e.currentTarget.style.background = '#fff';
              }}
            >
              {exp.name}
            </button>
          ))}
        </div>
      </div>

      {/* Add Custom Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px' }}>
              Ozel Uzmanlik Ekle
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', display: 'block' }}>
                  Uzmanlik Adi
                </label>
                <input
                  type="text"
                  value={newExpertise.name}
                  onChange={(e) => setNewExpertise(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ornek: Blockchain Development"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', display: 'block' }}>
                  Kategori
                </label>
                <select
                  value={newExpertise.category}
                  onChange={(e) => setNewExpertise(prev => ({ ...prev, category: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                >
                  {Object.keys(EXPERTISE_CATEGORIES).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', display: 'block' }}>
                  Yeterlilik Seviyesi
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(['beginner', 'intermediate', 'advanced', 'expert'] as const).map(level => (
                    <button
                      key={level}
                      onClick={() => setNewExpertise(prev => ({ ...prev, proficiency: level }))}
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '6px',
                        border: newExpertise.proficiency === level ? `2px solid ${PROFICIENCY_COLORS[level]}` : '1px solid #e2e8f0',
                        background: newExpertise.proficiency === level ? `${PROFICIENCY_COLORS[level]}15` : '#fff',
                        color: newExpertise.proficiency === level ? PROFICIENCY_COLORS[level] : 'var(--text-secondary)',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {PROFICIENCY_LABELS[level]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Iptal
              </button>
              <button
                onClick={handleAdd}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
